// ============================================================
// HOOKED — Wharf3D.js        HOOKED'S POCKET: THE LODGE, THE SAND BAR, ONLY WATER
//
// caP'n, 2026-09-25: "update Hooked so its 3D just like this using
// blender etc, replace the character for Lyraei and Laostre ez pz,
// and make sure its synced up so this exact model is over there in
// hooked … It should look like when you're playing hooked the plaza
// is just right there <3"
//
// ⚠ THE COMPATIBILITY CONTRACT (the same one Lake3D keeps). This
// takes the EXACT props of Lake.js / Lake3D.js — { angler, dispatch }
// — and mounts the exact same FishingOverlay (same fromBoat flag) and
// Wyllys. The swap in App.js is ONE LINE; Lake3D.js and Lake.js are
// UNTOUCHED and still work:
//     import { Lake3D as Lake } from "./Lake3D";    // the moonlit wharf
//     import { Wharf3D as Lake } from "./Wharf3D";  // Hooked's pocket
//
// WHAT IS THE SAME — THE HEART IS UNTOUCHED (PRIME §1.17): the catch
//   loop, the reel, the Outfitter, SCALES, every dispatch. Hooked's own
//   moon, water, sky, fog, fireflies, light rig, camera contract and
//   chrome — reused as they are (LakeWorld3D exports them).
// WHAT IS NEW — caP'n gets told, always:
//   1. THE PLACE. You start in Hooked's court facing the lodge (the
//      Pier's own Blender glb) — it looks like the Pier. Turn around:
//      the Pier is GONE. A boardwalk + gangway run down to the sand
//      bar (hooked_world.glb, Blender only), then water everywhere.
//      caP'n: "once you look around you realize the pier is entirely
//      gone, only water". The lodge's door is Wylly's. Through the DOCK
//      gate to the T-head: "Board the Boat" puts you in the skiff; sail
//      back under the davits: "Dock".
//   2. WORLD UNITS ARE THE PIER'S: 1 unit = 1 Pier tile, the planks at
//      y 0, the water at SEA_Y (−3.2). Collision + groundY come from the
//      lodge glb's walk-map (ie_shop) and the world glb's (ie_world).
//   3. THE BODY is Lyraei or Laostre (HeroAngler.js), chosen with the
//      pill top-left; Hooked's painted angler is the fail-safe.
//   4. THE DAVITS WORK (caP'n: "let's add the boat lowering"). Board and
//      the skiff is lowered on its slings to the water, the tackle goes
//      back up empty; Dock and the slings come down, catch it, and hoist
//      it home before you step out. Live ropes (DavitRopes) stand in for
//      the lodge's baked slings (hooked_sling) the whole time you're out.
//   5. FISH ANYWHERE NEAR WATER (caP'n: "fish off the dock or the little
//      sand bar … anywhere you're near water ;3"). On foot that's shore
//      fishing (fromBoat = false): the rare stuff still wants the boat.
//      You can walk the sand bar down to the waterline now.
// ============================================================

import { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

import { SD, GM, NIGHT_BG, textGlow, bevelPanel } from "./theme";
import { MobileDpad } from "./PlayerEntity";
import { FishingOverlay } from "./FishingSystem";
import { Wyllys } from "./Wyllys";
import { getOutfit, getItem, RADIANCE } from "./tackle";
import { WaterSurface, NightSky, UnstableHorizon, FarShore, Fireflies, SKY_R } from "./LakeWorld3D";
import { HeroAngler, loadBody, saveBody } from "./HeroAngler";
import { FrameWatch } from "./sculptGate";
import {
  HookedWorld3D, SEA_Y, HOOKED_FRAME, LODGE_META, WORLD_META, rectToWorld, localToWorld, inRect,
} from "./HookedWorld3D";
import * as CAM from "./ieCam";

// ── THE PLACE, all derived: the Pier's map + the lodge's own walk-map ──
const FR = HOOKED_FRAME;
const M = LODGE_META || {};
const toW = (r) => rectToWorld(FR, r);
const at = (lx, lz) => localToWorld(FR, lx, lz);
// ⚑ THE LAYOUT starts from pierLink.json (so you can walk from the first frame)
// and is REPLACED by the lodge glb's own walk-map the moment it loads — the
// glb is what you SEE, so it wins (QA 2026-09-25: a Blender rebuild without a
// re-sync would otherwise move the art but not the collision).
const LAYOUT = {};
function applyMeta(m) {
  m = m || {};
  LAYOUT.walk = (m.walk || []).map(toW);
  LAYOUT.solid = (m.solid || []).map(toW);
  LAYOUT.fish = (m.fish || []).map(toW);
  LAYOUT.board = m.board ? at(m.board[0], m.board[1]) : null;          // [x, z]
  LAYOUT.boatHome = m.boat ? at(m.boat[0], m.boat[2]) : null;          // [x, y, z]
  LAYOUT.doorstep = at(...(m.doorstep || [0, 0.9]));
  LAYOUT.slungY = m.boat ? m.boat[1] : -0.95;                         // the skiff hangs here in its davits
  // the davit blocks (world) the live ropes hang from; the hull's sling points ride the skiff
  LAYOUT.sling = m.sling && m.sling.block ? { block: m.sling.block.map((b) => { const w = at(b[0], b[2]); return [w[0], b[1], w[1]]; }) } : null;
  return JSON.stringify([m.walk, m.solid, m.fish, m.board, m.boat, m.doorstep, m.sling]);
}
let layoutSig = applyMeta(M);
// ⚑ THE WORLD's walk-map (hooked_world.glb ie_world): the court, the boardwalk, the
// gangway's slope, the sand bar (an ellipse + a height grid sampled off the same
// function that shaped it). Same rule: pierLink.json first, the glb's own on load.
const WORLD = {};
function applyWorld(w) {
  w = w || {};
  WORLD.walk = (w.walk || []).map(toW);
  WORLD.solid = (w.solid || []).map(toW);
  WORLD.block = (w.block || []).map(toW);
  WORLD.ramp = w.ramp ? { rect: toW(w.ramp.rect), z0: FR.z + w.ramp.z0, z1: FR.z + w.ramp.z1, y0: w.ramp.y0, y1: w.ramp.y1 } : null;
  WORLD.sand = w.sand ? { ...w.sand, cx: at(w.sand.cx, w.sand.cz)[0], cz: at(w.sand.cx, w.sand.cz)[1] } : null;
  // the lodge's apron (it stands on the Pier's deck behind the court's back edge)
  const m = M.mass || [-2.2, 0, -3.35, 2.2, 4, -0.02];
  WORLD.apron = toW([m[0] - 0.3, m[3] + 0.3, m[2] - 0.2, 0.1]);
  return JSON.stringify(w);
}
let worldSig = applyWorld(WORLD_META);
const sandE = (x, z) => { const s = WORLD.sand; return s ? Math.hypot((x - s.cx) / s.rx, (z - s.cz) / s.rz) : 99; };
// the ground under a foot: the planks (0), the gangway's slope, or the sand's height grid
export function groundY(x, z) {
  const r = WORLD.ramp;
  if (r && inRect(r.rect, x, z)) {
    const t = Math.min(1, Math.max(0, (z - r.z0) / (r.z1 - r.z0)));
    return r.y0 + (r.y1 - r.y0) * t;
  }
  const s = WORLD.sand;
  const e = s ? sandE(x, z) : 99;
  if (s && e > 1 && s.beach && e <= s.beach) return beachY(x, z, e);
  if (s && e <= 1 && s.grid) {
    const G = s.grid;
    const lx = x - FR.x, lz = z - FR.z;                     // the grid is in the lodge's frame (face S)
    const fx = Math.min(G.nx, Math.max(0, ((lx - G.x0) / (G.x1 - G.x0)) * G.nx));
    const fz = Math.min(G.nz, Math.max(0, ((lz - G.z0) / (G.z1 - G.z0)) * G.nz));
    const i = Math.min(G.nx - 1, Math.floor(fx)), j = Math.min(G.nz - 1, Math.floor(fz));
    const u = fx - i, v = fz - j, h = G.h, W = G.nx + 1;
    const a = h[j * W + i], b = h[j * W + i + 1], c = h[(j + 1) * W + i], d = h[(j + 1) * W + i + 1];
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  }
  return 0;
}
// the beach: the builder's own sand_h past the flat top (e > 1), easing down to its toe under the water
function beachY(x, z, e) {
  const s = WORLD.sand, lx = x - FR.x, lz = z - FR.z;
  const t = Math.min(1, (e - 1) / (s.beach - 1)), k = t * t * (3 - 2 * t);
  const ripple = 0.05 * Math.sin(lx * 0.9 + lz * 0.4) * Math.sin(lz * 0.7 - lx * 0.2);
  return s.y + ripple * (1 - k) - (s.y - s.toe) * k;
}
const SPAWN = at(0, 5.0);
// the lake: open water all round the lodge and its sand bar (Hooked's pocket: no Pier)
const LAKE = { x: FR.x, z: FR.z - 10, r: 125 };
const MOON_X = -380 * 0.18;               // LakeWorld3D's own moon (HORIZON_R × 0.18)
// the depth painting under the lake (LakeWorld3D reads a MAP for it): deep out, teal in by the Pier
const LAKE_MAP = Array.from({ length: 12 }, (_, r) => (r >= 9 ? "wwwwwwwwwwwwwwww" : "WWWWWWWWWWWWWWWW"));
// the fireflies drift over the court and the lodge, and over the sand bar
const FLY_MAP = Array.from({ length: 6 }, () => "LLLLLLLLL");
const FLY_MAP_SAND = Array.from({ length: 6 }, () => "LLLLLLLLLLLLL");

// ── THE DIALS ──────────────────────────────────────────────────
const WALK = {
  SPEED: 4.4,     // world units/sec on foot (Hooked's own)
  RUN:   1.7,     // × SPEED, on Shift
  BOAT:  9.0,     // the skiff is faster and heavier
  ACCEL: 12.0,
};
const R_FOOT = 0.3, R_BOAT = 0.62;
const REACH = { door: 1.5, board: 1.4, dock: 2.8 };

// Hooked's light rig, untouched — ONE KNOB: LIGHT.GAIN
const LIGHT = {
  GAIN: 1.0, ambient: 0.30, hemi: 0.42, moon: 0.95, rim: 0.34, exposure: 1.32,
  fogNear: 38, fogFar: 300,
};
const G = (v) => v * LIGHT.GAIN;

// ── COLLISION — exported so a gate can execute THE REAL FUNCTION ──
const footOn = (x, z) => LAYOUT.walk.some((r) => inRect(r, x, z)) || WORLD.walk.some((r) => inRect(r, x, z))
  || (WORLD.ramp && inRect(WORLD.ramp.rect, x, z)) || sandFoot(x, z);
// the sand: its flat top, and down the beach to the water's edge (dry to within WADE of the water)
const WADE = 0.12;
function sandFoot(x, z) {
  const s = WORLD.sand; if (!s) return false;
  const e = sandE(x, z);
  if (e <= s.walk) return true;
  if (!s.beach || e > s.beach) return false;
  return groundY(x, z) >= SEA_Y + WADE;
}
const wet = (x, z) => !LAYOUT.walk.some((w) => inRect(w, x, z, 0.12)) && !WORLD.block.some((b) => inRect(b, x, z))
  && !inRect(WORLD.apron, x, z) && !(WORLD.sand && sandE(x, z) <= WORLD.sand.block);
export function canStand(x, z, mode) {
  const r = mode === "boat" ? R_BOAT : R_FOOT;
  const pts = [[x, z], [x - r, z], [x + r, z], [x, z - r], [x, z + r]];
  if (mode === "boat") {
    if (Math.hypot(x - LAKE.x, z - LAKE.z) > LAKE.r) return false;
    if (z < LAKE.z - 48) return false;          // Hooked's far bank starts 53 out (FarShore): never sail into the land
    return pts.every(([px, pz]) => wet(px, pz));
  }
  return pts.every(([px, pz]) => footOn(px, pz))
    && !LAYOUT.solid.some((s) => inRect(s, x, z, r * 0.6)) && !WORLD.solid.some((s) => inRect(s, x, z, r * 0.6));
}
// a WALL SLIDE: a blocked diagonal keeps the free axis
export function slideMove(x, z, dx, dz, mode) {
  if (canStand(x + dx, z + dz, mode)) return [x + dx, z + dz];
  if (dx && canStand(x + dx, z, mode)) return [x + dx, z];
  if (dz && canStand(x, z + dz, mode)) return [x, z + dz];
  return [x, z];
}
// ⭐ FISH FROM ANYWHERE NEAR WATER. Open water = somewhere you can't stand that isn't the
// lodge, a solid, or the court/boardwalk's own footprint (the piles live under there).
const isWater = (x, z) => !footOn(x, z) && !inRect(WORLD.apron, x, z)
  && !WORLD.block.some((b) => inRect(b, x, z)) && !LAYOUT.walk.some((w) => inRect(w, x, z, 0.12))
  && !LAYOUT.solid.some((b) => inRect(b, x, z)) && !WORLD.solid.some((b) => inRect(b, x, z));
const FISH_REACH = 1.35, FISH_DIRS = 16;
// the water nearest the way you're facing, within a step and a cast; null when you're not near any
function waterYaw(x, z, yaw) {
  let best = null, bd = 9;
  for (let i = 0; i < FISH_DIRS; i++) {
    const a = (i / FISH_DIRS) * Math.PI * 2 - Math.PI;
    if (!isWater(x + Math.sin(a) * FISH_REACH, z + Math.cos(a) * FISH_REACH)) continue;
    let d = Math.abs(a - yaw) % (Math.PI * 2); if (d > Math.PI) d = Math.PI * 2 - d;
    if (d < bd) { bd = d; best = a; }
  }
  return best;
}
const onSand = (x, z) => !!WORLD.sand && sandE(x, z) <= (WORLD.sand.beach || WORLD.sand.walk);

// ⭐ THE DAVITS. One little state machine, read by the Controller, the skiff and the ropes:
//   down → the skiff lowers from its slings to the water (you're aboard, hands off)
//   release → the empty tackle goes back up while you sail off
//   catch → Dock: the skiff glides home under the davits, the slings come down to it
//   up → hoisted home; then you step out onto the T-head
const DAVIT = { DOWN: 2.6, RELEASE: 1.0, CATCH: 1.2, UP: 2.4 };
const ease = (t) => { t = Math.min(1, Math.max(0, t)); return t * t * (3 - 2 * t); };
function runDavits(D, dt, p, yawRef) {
  const home = LAYOUT.boatHome, hy = LAYOUT.slungY;
  D.t += dt;
  const finish = () => { D.phase = null; D.t = 0; D.attach = 0; const f = D.done; D.done = null; if (f) f(); };
  if (!home) return finish();
  if (D.phase === "down") {
    p.set(home[0], hy + (SEA_Y - hy) * ease(D.t / DAVIT.DOWN), home[1]); D.attach = 1;
    if (D.t >= DAVIT.DOWN) { D.phase = "release"; D.t = 0; }
  } else if (D.phase === "catch") {
    const f = Math.min(1, dt * 4);
    p.x += (home[0] - p.x) * f; p.z += (home[1] - p.z) * f; p.y = SEA_Y;
    yawRef.current = Math.PI;                                        // bow out, the way it hangs
    D.attach = ease(D.t / DAVIT.CATCH);
    if (D.t >= DAVIT.CATCH) { p.set(home[0], SEA_Y, home[1]); D.phase = "up"; D.t = 0; D.attach = 1; }
  } else {
    p.set(home[0], SEA_Y + (hy - SEA_Y) * ease(D.t / DAVIT.UP), home[1]); D.attach = 1;
    if (D.t >= DAVIT.UP) finish();
  }
}
const near = (p, x, z, r) => !!p && (p[0] - x) ** 2 + (p[1] - z) ** 2 <= r * r;
if (typeof window !== "undefined") {
  window.__wharf = { canStand, groundY, LAYOUT, WORLD, SPAWN,
    get BOARD() { return LAYOUT.board; }, get BOAT_HOME() { return LAYOUT.boatHome; }, get DOORSTEP() { return LAYOUT.doorstep; } };
}

// ════════════════════════════════════════════════════════════
// THE CONTROLLER — inside the Canvas, returns null, only ever
// announces a changed PROMPT.
// ════════════════════════════════════════════════════════════
function Controller({ posRef, yawRef, speedRef, keysRef, mode, frozen, onPrompt, davitRef, fishYawRef }) {
  const promptId = useRef("");
  const vel = useRef(0);

  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05);
    // the davits have the boat: hands off, no prompt, until it's on the water (or home)
    const D = davitRef.current;
    if (D.phase === "down" || D.phase === "catch" || D.phase === "up") {
      runDavits(D, dt, posRef.current, yawRef);
      vel.current = 0; speedRef.current = 0;
      if (promptId.current) { promptId.current = ""; onPrompt(null); }
      return;
    }
    if (D.phase === "release") {
      D.t += dt; D.attach = 1 - ease(D.t / DAVIT.RELEASE);
      if (D.t >= DAVIT.RELEASE) { D.phase = null; D.t = 0; D.attach = 0; }
    }
    const k = keysRef.current;              // READ FRESH. Never captured.
    let ix = 0, iy = 0;
    if (!frozen) {
      if (k["ArrowUp"]    || k["w"] || k["W"]) iy -= 1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) iy += 1;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) ix -= 1;
      if (k["ArrowRight"] || k["d"] || k["D"]) ix += 1;
    }
    const mag = Math.hypot(ix, iy);
    if (mag > 1) { ix /= mag; iy /= mag; }
    const r = CAM.rotateInput(ix, iy);       // ⚑ MOVEMENT FOLLOWS THE EYE

    const running = !!(k["Shift"] || k["shift"]);
    const top = (mode === "boat" ? WALK.BOAT : WALK.SPEED) * (running && mode !== "boat" ? WALK.RUN : 1);
    const want = Math.hypot(r.dx, r.dy) > 0.001 ? top : 0;
    const accel = mode === "boat" ? 3.4 : WALK.ACCEL;
    vel.current += (want - vel.current) * Math.min(1, dt * accel);
    if (vel.current < 0.02) vel.current = 0;

    const p = posRef.current;
    if (want > 0) {
      const len = Math.hypot(r.dx, r.dy) || 1;
      const step = vel.current * dt;
      const [nx, nz] = slideMove(p.x, p.z, (r.dx / len) * step, (r.dy / len) * step, mode);
      p.x = nx; p.z = nz;
      yawRef.current = Math.atan2(r.dx / len, r.dy / len);
    }
    const targetY = mode === "boat" ? SEA_Y : groundY(p.x, p.z);
    p.y += (targetY - p.y) * Math.min(1, dt * 14);
    speedRef.current = vel.current / WALK.SPEED;

    // ── what's near — computed every frame, ANNOUNCED only on change ──
    let id = "", verb = "", label = "", color = GM.goldBright;
    if (!frozen) {
      if (mode === "walk") {
        if (near(LAYOUT.doorstep, p.x, p.z, REACH.door)) {
          id = "shack"; verb = "Wylly's Bait & Tackle"; label = "rods, threads & stranger things";
        } else if (near(LAYOUT.board, p.x, p.z, REACH.board)) {
          id = "board"; verb = "Board the Boat"; label = "the water is deeper out there"; color = SD.tealLight;
        } else if (LAYOUT.fish.some((f) => inRect(f, p.x, p.z))) {
          id = "fish"; verb = "Fish"; label = "the shallows and the drift";
          fishYawRef.current = null;                                   // the T-head: cast the way you face
        } else {
          const wy = waterYaw(p.x, p.z, yawRef.current || 0);
          if (wy !== null) {
            fishYawRef.current = wy;
            if (onSand(p.x, p.z)) { id = "fish_sand"; verb = "Fish"; label = "the shallows off the sand"; }
            else { id = "fish_rail"; verb = "Fish"; label = "a line off the boards"; }
          }
        }
      } else if (D.phase === "release") {
        // the tackle's still going up: nothing to do yet
      } else if (near(LAYOUT.boatHome, p.x, p.z, REACH.dock)) {
        id = "dock"; verb = "Dock"; label = "back to the boards"; color = SD.tealLight;
      } else {
        id = "fish"; verb = "Fish"; label = "the deep answers out here";
      }
    }
    if (id !== promptId.current) {
      promptId.current = id;
      onPrompt(id ? { id, verb, label, color } : null);
    }
  });
  return null;
}

// ════════════════════════════════════════════════════════════
// THE CAMERA — Lake3D's contract, all four pieces, unchanged but
// for the floor: the lens never goes under the lake (SEA_Y now).
// ════════════════════════════════════════════════════════════
const PRESET = {
  walk: { back: 9.4,  up: 4.6, look: 1.35 },
  boat: { back: 13.0, up: 6.4, look: 1.10 },
};

function CameraRig({ posRef, yawRef, mode }) {
  const controls = useRef();
  const { camera } = useThree();
  const posed = useRef(false);
  const last  = useRef(new THREE.Vector3(0, 0, 0));
  const seenReset = useRef(CAM.getResetCount());
  const seenPose  = useRef(CAM.getPoseRelease());
  const goal = useMemo(() => new THREE.Vector3(), []);
  const tgt  = useMemo(() => new THREE.Vector3(), []);
  const tmp  = useMemo(() => new THREE.Vector3(), []);
  const first = useRef(true);

  useEffect(() => () => CAM.resetForward(), []);

  useFrame(() => {
    const c = controls.current;
    const p = posRef.current;
    if (!c || !p) return;
    const K = PRESET[mode === "boat" ? "boat" : "walk"];
    const mult = CAM.getMult();
    if (CAM.getResetCount() !== seenReset.current) { seenReset.current = CAM.getResetCount(); posed.current = false; }
    if (CAM.getPoseRelease() !== seenPose.current) { seenPose.current = CAM.getPoseRelease(); posed.current = false; }

    tgt.set(p.x, p.y + K.look, p.z);
    tmp.copy(p).sub(last.current);
    last.current.copy(p);

    if (posed.current) {
      camera.position.add(tmp);
      c.target.add(tmp);
    } else {
      const back = K.back * mult;
      const up   = K.up * (0.55 + 0.45 * mult);
      const y = yawRef.current || 0;
      goal.set(p.x - Math.sin(y) * back, p.y + up, p.z - Math.cos(y) * back);
      // no fly-in from the origin; dev QA can ask for a snap too (window.__wharfSnap = true)
      const snap = typeof window !== "undefined" && window.__wharfSnap;
      if (first.current || snap) { camera.position.copy(goal); c.target.copy(tgt); first.current = false; if (snap) window.__wharfSnap = false; }
      camera.position.lerp(goal, 0.055);
      c.target.lerp(tgt, 0.12);
    }
    // the lens never goes under what you stand on: the planks on foot, the lake afloat
    const floor = mode === "boat" ? SEA_Y + 0.85 : p.y + 0.6;
    if (camera.position.y < floor) camera.position.y = floor;
    c.update();
    tmp.copy(c.target).sub(camera.position);
    tmp.y = 0;
    if (tmp.lengthSq() > 1e-6) CAM.setForward(tmp.x, tmp.z);
  });

  return (
    <OrbitControls
      ref={controls}
      enableDamping
      dampingFactor={0.075}
      enablePan={false}
      minDistance={2.4}
      maxDistance={46}
      maxPolarAngle={Math.PI / 1.9}
      onStart={() => { posed.current = true; }}
    />
  );
}

// ════════════════════════════════════════════════════════════
// THE SKIFF YOU SAIL — the lodge's own skiff (one object in its
// glb), moving the way Boat3D moves, bobbing on the Pier's sea.
// ════════════════════════════════════════════════════════════
function PierSkiff({ posRef, yawRef, speedRef, skiff, seatRef }) {
  const root = useRef();
  const v = useMemo(() => new THREE.Vector3(), []);
  // launch already facing out (no 180° swing on boarding), and let go of the seat on the way out
  useEffect(() => {
    if (root.current) root.current.rotation.y = yawRef.current || 0;
    const seat = seatRef.current;
    return () => { seat.on = false; };
  }, [yawRef, seatRef]);
  useFrame((state, dt) => {
    const g = root.current, p = posRef.current;
    if (!g || !p) return;
    const d = Math.min(dt, 0.05);
    let diff = (yawRef.current || 0) - g.rotation.y;
    while (diff >  Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    g.rotation.y += diff * Math.min(1, d * 6);          // a boat turns like a boat
    const t = state.clock.elapsedTime, sp = speedRef.current || 0;
    const wetK = Math.max(0, Math.min(1, 1 - (p.y - SEA_Y) / 0.3));   // 1 afloat, 0 hanging in the davits
    g.position.set(p.x, p.y + (Math.sin(t * 1.15) * 0.045 + Math.sin(t * 0.61) * 0.03) * wetK, p.z);
    g.rotation.z = (Math.sin(t * 0.9) * 0.045 - sp * 0.03) * wetK + Math.sin(t * 1.7) * 0.018 * (1 - wetK);
    g.rotation.x = (Math.sin(t * 1.3) * 0.028 + sp * 0.02) * wetK;
    // the seat: on the floorboards behind the middle thwart, facing the bow
    g.updateMatrixWorld();
    v.set(0, -0.04, -0.3).applyMatrix4(g.matrixWorld);
    const s = seatRef.current; s.on = true; s.x = v.x; s.y = v.y; s.z = v.z; s.yaw = g.rotation.y;
  });
  return (
    <group ref={root}>
      {skiff ? <primitive object={skiff} /> : (
        // the lodge hasn't handed its skiff over yet: Boat3D's own hull stands in
        <mesh position={[0, 0.1, 0]}><cylinderGeometry args={[0.62, 0.44, 0.34, 10]} /><meshStandardMaterial color="#4a3418" roughness={0.82} /></mesh>
      )}
      {/* the bow lantern still lights the water, as Boat3D's does — at the bow (+z) */}
      <pointLight position={[0, 1.1, 1.05]} intensity={4.2} distance={12} decay={1.4} color="#ffe9a8" />
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE DAVITS' ROPES — live while you're out (the lodge's baked slings,
// hooked_sling, are hidden then). Each block drops two ropes: to the
// hull's sling points while it's attached, to an empty hook when not.
// ════════════════════════════════════════════════════════════
const ROPE_MAT = new THREE.MeshStandardMaterial({ color: "#c9b48a", roughness: 0.95 });
const HOOK_MAT = new THREE.MeshStandardMaterial({ color: "#3a3430", roughness: 0.45, metalness: 0.55 });
const ROPE_GEO = new THREE.CylinderGeometry(0.014, 0.014, 1, 5, 1, true);
const HOOK_GEO = new THREE.SphereGeometry(0.055, 8, 6);
const Y_UP = new THREE.Vector3(0, 1, 0);
function DavitRopes({ skiff, davitRef }) {
  const ropes = useRef([]), hooks = useRef([]);
  const v = useMemo(() => ({ a: new THREE.Vector3(), b: new THREE.Vector3(), d: new THREE.Vector3(), h: new THREE.Vector3(), m: new THREE.Vector3(), t: new THREE.Vector3() }), []);
  const n = LAYOUT.sling ? LAYOUT.sling.block.length : 0;
  useFrame(() => {
    const sl = LAYOUT.sling, holds = skiff && skiff.userData && skiff.userData.holds;
    const ok = sl && holds && holds.length >= sl.block.length * 2;
    const k = davitRef.current.attach;
    for (let i = 0; i < n; i++) {
      const B = ok && sl.block[i];
      v.m.set(0, 0, 0);
      for (let j = 0; j < 2; j++) {
        const r = ropes.current[i * 2 + j]; if (!r) continue;
        if (!B) { r.visible = false; continue; }
        v.a.set(B[0], B[1], B[2]);
        v.d.set(B[0], B[1] - 0.72, B[2]);                     // an empty tackle hangs just under the block
        holds[i * 2 + j].getWorldPosition(v.h);
        v.b.copy(v.d).lerp(v.h, k);
        v.m.add(v.b);
        v.t.subVectors(v.b, v.a);
        const L = v.t.length();
        r.position.copy(v.a).addScaledVector(v.t, 0.5);
        r.scale.set(1, Math.max(L, 0.001), 1);
        if (L > 1e-5) r.quaternion.setFromUnitVectors(Y_UP, v.t.multiplyScalar(1 / L));
        r.visible = true;
      }
      const hk = hooks.current[i];
      if (hk) { hk.visible = !!B && k < 0.95; if (B) hk.position.copy(v.m.multiplyScalar(0.5)); }
    }
  });
  return (
    <group>
      {Array.from({ length: n * 2 }, (_, i) => <mesh key={i} ref={(el) => { ropes.current[i] = el; }} geometry={ROPE_GEO} material={ROPE_MAT} castShadow />)}
      {Array.from({ length: n }, (_, i) => <mesh key={"h" + i} ref={(el) => { hooks.current[i] = el; }} geometry={HOOK_GEO} material={HOOK_MAT} />)}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE SCENE — memoised; the prompt pill never reconciles the tree.
// ════════════════════════════════════════════════════════════
const Scene = memo(function Scene({ posRef, yawRef, speedRef, keysRef, mode, frozen, outfit, gender, onPrompt, davitRef, fishYawRef }) {
  const inBoat = mode === "boat";
  const [skiff, setSkiff] = useState(null);
  const seatRef = useRef({ on: false, x: 0, y: 0, z: 0, yaw: 0 });
  const onWorld = useCallback((meta) => {
    if (!meta) return;
    const sig = applyWorld(meta);
    if (sig !== worldSig) console.info("[hooked] hooked_world.glb's walk-map differs from pierLink.json — walking the glb's (re-run tools/syncPier.mjs)");
    worldSig = sig;
  }, []);
  const onLodge = useCallback((meta, sk) => {
    if (meta) {
      const sig = applyMeta(meta);
      if (sig !== layoutSig) console.info("[hooked] the lodge glb's walk-map differs from pierLink.json — walking the glb's (re-run tools/syncPier.mjs)");
      layoutSig = sig;
    }
    setSkiff(sk);
  }, []);
  // the moon keys the lodge, not the world origin
  const moonTarget = useMemo(() => { const o = new THREE.Object3D(); o.position.set(FR.x - 4, 0, FR.z - 8); return o; }, []);
  return (
    <>
      <FrameWatch />
      <ambientLight intensity={G(LIGHT.ambient)} color="#7f93a8" />
      <hemisphereLight args={[SD.void5, GM.pageDeep, G(LIGHT.hemi)]} />
      <primitive object={moonTarget} />
      <directionalLight
        target={moonTarget}
        position={[FR.x - 4 - 68, 96, FR.z - 8 - 180]}
        intensity={G(LIGHT.moon)}
        color="#cfe0dd"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-left={-40}
        shadow-camera-right={40}
        shadow-camera-top={40}
        shadow-camera-bottom={-40}
        shadow-camera-far={320}
        shadow-bias={-0.0012}
      />
      <directionalLight position={[FR.x + 54, 26, FR.z + 70]} intensity={G(LIGHT.rim)} color={SD.purpleLight} />
      <fog attach="fog" args={[SD.void2, LIGHT.fogNear, LIGHT.fogFar]} />

      {/* Hooked's own night and water, laid under the Pier */}
      <group position={[LAKE.x, SEA_Y, LAKE.z]}>
        <NightSky moonWorldX={MOON_X} />
        <UnstableHorizon />
        <FarShore MAP={LAKE_MAP} />
        <WaterSurface MAP={LAKE_MAP} moonWorldX={MOON_X} />
      </group>
      <group position={[FR.x, 0, FR.z + 2]}><Fireflies MAP={FLY_MAP} /></group>
      {WORLD.sand && <group position={[WORLD.sand.cx, WORLD.sand.y, WORLD.sand.cz]}><Fireflies MAP={FLY_MAP_SAND} /></group>}

      {/* Hooked's own pocket: the lodge, its patch of Pier, the sand bar — and water */}
      <HookedWorld3D inBoat={inBoat} onLodge={onLodge} onWorld={onWorld} posRef={posRef} />

      {inBoat && <PierSkiff posRef={posRef} yawRef={yawRef} speedRef={speedRef} skiff={skiff} seatRef={seatRef} />}
      {/* after the skiff, so the ropes tie on to where it is THIS frame */}
      {inBoat && <DavitRopes skiff={skiff} davitRef={davitRef} />}
      {/* ONE body for the whole session: afloat it rides the skiff's seat */}
      <HeroAngler gender={gender} posRef={posRef} yawRef={yawRef} speedRef={speedRef} seatRef={seatRef}
        afloat={inBoat} mode={frozen ? "cast" : "walk"} outfit={outfit} />

      <Controller posRef={posRef} yawRef={yawRef} speedRef={speedRef}
        keysRef={keysRef} mode={mode} frozen={frozen} onPrompt={onPrompt} davitRef={davitRef} fishYawRef={fishYawRef} />
      <CameraRig posRef={posRef} yawRef={yawRef} mode={mode} />
    </>
  );
}, (a, b) => a.mode === b.mode && a.frozen === b.frozen && a.outfit === b.outfit && a.gender === b.gender);

// ════════════════════════════════════════════════════════════
// THE SCREEN
// ════════════════════════════════════════════════════════════
export function Wharf3D({ angler, dispatch }) {
  const [mode, setMode]         = useState("walk");   // walk | boat
  const [fishing, setFishing]   = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [prompt, setPrompt]     = useState(null);
  const [view, setViewState]    = useState(CAM.getView());
  const [gender, setGender]     = useState(loadBody);

  // ⚠ keysRef is created ONCE and only ever MUTATED
  const keysRef  = useRef({});
  const posRef   = useRef(new THREE.Vector3(SPAWN[0], 0, SPAWN[1]));
  const yawRef   = useRef(Math.PI);                    // facing the lodge (north)
  const speedRef = useRef(0);
  const promptRef = useRef(null);
  const davitRef = useRef({ phase: null, t: 0, attach: 0, done: null });   // THE DAVITS (see runDavits)
  const fishYawRef = useRef(null);                                         // which way the water is, on foot

  const frozen = fishing || shopOpen;
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  const outfit = useMemo(() => getOutfit(angler), [angler]);

  const clearKeys = useCallback(() => {
    const k = keysRef.current;
    for (const key of Object.keys(k)) k[key] = false;   // MUTATE
  }, []);

  const boardBoat = useCallback(() => {
    const home = LAYOUT.boatHome; if (!home) return;
    // aboard in the slings, then the davits lower you (runDavits: "down")
    posRef.current.set(home[0], LAYOUT.slungY, home[1]);
    yawRef.current = Math.PI;          // bow out, into the deep (north)
    davitRef.current = { phase: "down", t: 0, attach: 1, done: null };
    setMode("boat"); clearKeys();
  }, [clearKeys]);
  const disembark = useCallback(() => {
    const b = LAYOUT.board; if (!b) return;
    posRef.current.set(b[0], 0, b[1]);
    yawRef.current = Math.PI / 2;      // back along the T-head
    setMode("walk"); clearKeys();
  }, [clearKeys]);
  // Dock: the davits catch the skiff and hoist it home, THEN you step out
  const dockBoat = useCallback(() => {
    davitRef.current = { phase: "catch", t: 0, attach: 0, done: disembark };
    clearKeys();
  }, [disembark, clearKeys]);
  const casting = useRef(false);
  const activate = useCallback(() => {
    const p = promptRef.current;
    if (!p) return;
    if (p.id === "board") boardBoat();
    else if (p.id === "dock") dockBoat();
    else if (p.id.startsWith("fish")) {
      // on the shore: turn to the water first, then cast (a beat so you see her turn)
      const wy = p.id === "fish" ? null : fishYawRef.current;
      if (wy === null || wy === undefined) { setFishing(true); return; }
      if (casting.current) return;
      casting.current = true; yawRef.current = wy;
      setTimeout(() => { casting.current = false; setFishing(true); }, 220);
    }
    else if (p.id === "shack") setShopOpen(true);
  }, [boardBoat, dockBoat]);

  promptRef.current = prompt;

  useEffect(() => CAM.subscribe(() => setViewState(CAM.getView())), []);
  const cycleView = useCallback(() => {
    const i = CAM.VIEWS.findIndex((v) => v.id === CAM.getView());
    CAM.setView(CAM.VIEWS[(i + 1) % CAM.VIEWS.length].id);
  }, []);
  const doDefault = useCallback(() => CAM.requestDefault(), []);
  const flipBody = useCallback(() => setGender((g) => { const n = g === "m" ? "f" : "m"; saveBody(n); return n; }), []);

  useEffect(() => {
    const down = (e) => {
      const k = keysRef.current;
      k[e.key] = true;
      if (e.key === "Shift") k.Shift = true;
      if (frozenRef.current) return;             // the overlay owns Space
      if (e.key === " " || e.key === "Enter" || e.key === "e" || e.key === "E") {
        if (e.repeat) return;
        e.preventDefault();
        activate();
      }
      if (e.key === "c" || e.key === "C") { if (!e.repeat) cycleView(); }
      if (e.key === "v" || e.key === "V") { if (!e.repeat) doDefault(); }
    };
    const up = (e) => { keysRef.current[e.key] = false; if (e.key === "Shift") keysRef.current.Shift = false; };
    const blur = () => clearKeys();
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, [activate, cycleView, doDefault, clearKeys]);

  // dev: walk anywhere by hand (?preview-style QA): __wharf.tp(x, z) / __wharf.pos()
  useEffect(() => {
    if (typeof window === "undefined" || !window.__wharf) return;
    window.__wharf.tp = (x, z, y = 0) => posRef.current.set(x, y, z);
    window.__wharf.pos = () => ({ x: +posRef.current.x.toFixed(2), y: +posRef.current.y.toFixed(2), z: +posRef.current.z.toFixed(2) });
    window.__wharf.act = () => activate();
    window.__wharf.face = (a) => { yawRef.current = a; };
    window.__wharf.prompt = () => promptRef.current;
    window.__wharf.davit = () => ({ ...davitRef.current, done: !!davitRef.current.done });
  }, [activate]);

  const onPrompt = useCallback((p) => setPrompt(p), []);
  const title = getItem(angler.wardrobe?.title);
  const titleColor = title ? (RADIANCE[title.radiance] || RADIANCE.common).color : GM.gold;

  return (
    <div style={{ position: "fixed", inset: 0, background: NIGHT_BG, overflow: "hidden" }}>
      <Canvas
        shadows
        frameloop={frozen ? "never" : "always"}
        dpr={[1, 1.5]}
        camera={{ fov: 52, near: 0.3, far: SKY_R * 1.4, position: [SPAWN[0], 5, SPAWN[1] + 9] }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = LIGHT.exposure;
        }}
        style={{ position: "absolute", inset: 0,
          filter: frozen ? "blur(3px) saturate(0.8) brightness(0.7)" : "none",
          transition: "filter 260ms ease" }}
      >
        <color attach="background" args={[SD.void0]} />
        <Scene
          posRef={posRef} yawRef={yawRef} speedRef={speedRef}
          keysRef={keysRef} mode={mode} frozen={frozen} outfit={outfit} gender={gender} onPrompt={onPrompt}
          davitRef={davitRef} fishYawRef={fishYawRef}
        />
      </Canvas>

      {/* ── THE GRADE — Lake3D's, unchanged ── */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 60,
        background: `radial-gradient(ellipse at 50% 44%, rgba(0,0,0,0) 42%, rgba(1,2,8,0.42) 78%, rgba(1,2,8,0.75) 100%)`,
        mixBlendMode: "multiply",
      }} />
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 61,
        background:
          `radial-gradient(ellipse at 28% 18%, ${SD.moonHalo}12 0%, transparent 46%),
           radial-gradient(ellipse at 50% 88%, ${GM.gold}0d 0%, transparent 52%)`,
        mixBlendMode: "screen",
      }} />

      {!frozen && (
        <div style={pill(GM.goldBright, GM.gold, { top: 16, left: 18, fontSize: 12, letterSpacing: 4 })}>
          ◆ THE LAKE
        </div>
      )}
      {!frozen && title && (
        <div style={pill(titleColor, titleColor, { top: 52, left: 18, fontSize: 9, letterSpacing: 3 })}>
          {title.glyph} {title.name}
        </div>
      )}
      {/* who you are on the water — Lyraei or Laostre (remembered on this device) */}
      {!frozen && (
        <button onClick={flipBody} style={{ ...camBtn(false), position: "fixed", top: title ? 88 : 52, left: 18, zIndex: 400 }}>
          {gender === "m" ? "LAOSTRE" : "LYRAEI"} ⇄
        </button>
      )}

      {!frozen && (
        <div style={{ position: "fixed", top: 14, right: 16, zIndex: 400, display: "flex", gap: 6, alignItems: "center" }}>
          {CAM.VIEWS.map((v) => (
            <button key={v.id} onClick={() => CAM.setView(v.id)} style={camBtn(view === v.id)}>
              {v.label}
            </button>
          ))}
          <button onClick={doDefault} style={camBtn(false)}>DEFAULT</button>
        </div>
      )}

      {!frozen && prompt && (
        <div style={{
          position: "fixed", left: "50%", bottom: 34, transform: "translateX(-50%)",
          zIndex: 300, pointerEvents: "none", textAlign: "center",
          padding: "10px 22px", borderRadius: 14,
          background: `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
          border: `1px solid ${prompt.color}55`,
          ...bevelPanel(prompt.color),
          boxShadow: `0 8px 30px rgba(0,0,0,0.8)`,
        }}>
          <div style={{ fontFamily: GM.fontDisplay, fontSize: 14, letterSpacing: 2.6, color: prompt.color, ...textGlow(prompt.color) }}>
            {prompt.verb}
          </div>
          <div style={{ fontFamily: GM.fontBody, fontSize: 11, color: GM.inkDim, marginTop: 2 }}>
            {prompt.label}
          </div>
          <div style={{ marginTop: 6, fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 2, color: GM.gold, opacity: 0.85 }}>
            SPACE
          </div>
        </div>
      )}

      {!frozen && !prompt && (
        <div style={{
          position: "fixed", left: "50%", bottom: 26, transform: "translateX(-50%)",
          zIndex: 300, pointerEvents: "none",
          fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 2.4, color: GM.inkBrown,
        }}>
          WASD MOVE · SHIFT RUN · DRAG TO LOOK · C VIEW · V DEFAULT
        </div>
      )}

      {!frozen && <MobileDpad keysRef={keysRef} />}

      {shopOpen && <Wyllys angler={angler} dispatch={dispatch} onClose={() => setShopOpen(false)} />}

      {fishing && (
        <FishingOverlay
          angler={angler}
          dispatch={dispatch}
          fromBoat={mode === "boat"}
          onExit={() => setFishing(false)}
        />
      )}
    </div>
  );
}

// ── chrome helpers (Lake3D's) ─────────────────────────────────
function pill(color, glow, extra) {
  return {
    position: "fixed", zIndex: 210, pointerEvents: "none",
    fontFamily: GM.fontDisplay, color, ...textGlow(glow),
    padding: "7px 16px", borderRadius: 999,
    background: `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
    border: `1px solid ${glow}44`,
    ...bevelPanel(glow),
    ...extra,
  };
}

function camBtn(active) {
  return {
    fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 2,
    padding: "7px 12px", borderRadius: 9, cursor: "pointer",
    color: active ? GM.pageDeep : GM.goldBright,
    background: active
      ? `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold})`
      : `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
    border: `1px solid ${GM.gold}${active ? "" : "55"}`,
    boxShadow: `0 2px 10px rgba(0,0,0,0.7)`,
    userSelect: "none", WebkitUserSelect: "none",
  };
}

export default Wharf3D;
